import React, { useState } from 'react';
import { ChevronDown, Calendar } from 'lucide-react';
import Section from '../ui/section';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Helmet } from 'react-helmet-async';
import ATSCalendarModal from '@/components/ats/ATSCalendarModal';
import { trackEvent } from '@/utils/analytics';

interface FAQItem {
  question: string;
  answer: string;
}

const corporateFAQs: FAQItem[] = [
  {
    question: "¿Cuál es el ROI típico de implementar Nexus?",
    answer: "Nuestros clientes reportan un ROI promedio de 3.5x en el primer año. Esto incluye reducción de costos de agencias externas (40-60%), ahorro en tiempo de los equipos de RRHH (15-20 horas/semana), y reducción de rotación temprana (hasta 25%). Ofrecemos una calculadora de ahorro personalizada para estimar tu ROI específico."
  },
  {
    question: "¿Cuánto tiempo toma ver resultados después de la implementación?",
    answer: "Los resultados son inmediatos en términos de eficiencia operativa. El primer mes verás reducción en tiempo administrativo. A los 3 meses, tendrás datos suficientes para medir mejoras en tiempo de contratación (típicamente 40-50% menos). A los 6 meses, podrás evaluar impacto en calidad de contratación y retención."
  },
  {
    question: "¿Cómo se compara Nexus con otras soluciones ATS del mercado?",
    answer: "Nexus es el único ecosistema en Centroamérica que integra ATS + Psicometría + Job Boards + Hunting en una sola plataforma. A diferencia de soluciones genéricas, estamos optimizados para el mercado local con integración nativa a Elempleo.com (+1M candidatos) y soporte en español con equipo local."
  },
  {
    question: "¿Qué nivel de soporte incluye la implementación?",
    answer: "Todos los planes incluyen onboarding guiado, capacitación para tu equipo, y soporte por chat/email. Los planes Corporativo y Enterprise incluyen gerente de cuenta dedicado, SLA garantizado de 4 horas, capacitación ilimitada, y revisiones trimestrales de optimización."
  },
  {
    question: "¿Cómo garantizan la seguridad de los datos de candidatos?",
    answer: "Cumplimos con estándares internacionales de seguridad (SOC 2, GDPR). Los datos están encriptados en tránsito y reposo, con servidores en la región. Ofrecemos control granular de permisos, auditoría de accesos, y cumplimiento con leyes locales de protección de datos. Podemos firmar acuerdos de procesamiento de datos (DPA) para clientes enterprise."
  },
  {
    question: "¿Puedo integrar Nexus con mi sistema actual de RRHH/ERP?",
    answer: "Sí. Ofrecemos integraciones pre-construidas con SAP SuccessFactors, Workday, Oracle HCM, y los principales HRIS. Para sistemas propietarios, nuestra API REST permite integraciones personalizadas. El equipo técnico puede completar integraciones complejas en 2-4 semanas."
  },
  {
    question: "¿Qué pasa si necesito escalar rápidamente (ej: temporada alta)?",
    answer: "La plataforma escala automáticamente sin costo adicional de infraestructura. Puedes agregar usuarios y publicar ofertas ilimitadas en planes Corporativo+. Para contrataciones masivas, activamos funcionalidades especiales como screening automatizado con IA y programación de entrevistas en lote."
  },
  {
    question: "¿Ofrecen prueba piloto antes de comprometer presupuesto anual?",
    answer: "Sí. Ofrecemos prueba gratuita de 14 días con acceso completo. Para empresas enterprise, podemos estructurar un piloto de 60-90 días con métricas de éxito definidas antes de firmar contrato anual. No hay compromiso hasta que veas resultados."
  },
];

// Generate FAQ Schema for SEO
const generateFAQSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": corporateFAQs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

const CorporateFAQSection: React.FC = () => {
  const [calendarOpen, setCalendarOpen] = useState(false);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generateFAQSchema())}
        </script>
      </Helmet>
      
      <Section id="faq-corporativo" className="bg-gradient-to-br from-background to-primary/5 !py-[80px]" >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-wider text-white mb-4" style={{ background: "#620AE2" }}>
              PREGUNTAS FRECUENTES
            </span>
            <h2 className="font-exo font-bold mt-2 mb-4" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#1A1A2E" }}>
              Lo que los líderes de RRHH preguntan antes de decidir
            </h2>
            <p className="text-lg text-muted-foreground">
              Respuestas directas para tomar decisiones informadas
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {corporateFAQs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:border-primary/30 data-[state=open]:shadow-lg transition-all duration-300"
              >
                <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary py-5 [&[data-state=open]>svg]:rotate-180">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA Section */}
          <div className="mt-12 text-center bg-card border border-border rounded-2xl p-8">
            <h3 className="text-xl font-bold text-foreground mb-3">
              ¿Tiene más preguntas?
            </h3>
            <p className="text-muted-foreground mb-6">
              Agende una llamada de 15 minutos y resuelva todas sus dudas con un especialista.
            </p>
            <Button 
              size="lg"
              className="bg-primary text-white hover:bg-primary/90 hover:text-white"
              onClick={() => {
                trackEvent('cta_faq_call_click', 'CTA', 'FAQ - Agendar llamada sin compromiso');
                setCalendarOpen(true);
              }}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Agendar llamada sin compromiso
            </Button>
          </div>
        </div>
      </Section>

      <ATSCalendarModal
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        title="Agendar una llamada"
        subtitle="Resuelve todas tus dudas con un especialista en 15 minutos"
      />
    </>
  );
};

export default CorporateFAQSection;
